import {AxiosRequestConfig} from "axios";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import GUIActionParamsAndRow from "@/utils/moreMenu/GUIActionParamsAndRow.ts";
import GUIActionResultType from "@/utils/constants/GUIActionResultType.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import NamaPrinting from "@/utils/common/NamaPrinting.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import Translator from "@/utils/common/Translator.ts";
import AfterPerformBehavior from "@/utils/constants/AfterPerformBehavior.ts";
import ActionPerformerHelper from "@/utils/moreMenu/ActionPerfomerHelper.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import {Dialog} from "quasar";
import QuickCreatorDialog from "@/components/dialogs/QuickCreatorDialog.vue";
import ListDialog from "@/components/dialogs/ListDialog.vue";
import {ActionPlaceholder} from "@/utils/moreMenu/ActionHandler.ts";
import ProgressDialog from "@/components/dialogs/ProgressDialog.vue";
import {ActionInProgress} from "@/utils/presenters/interfaces.ts";
import TaskPerformerHelper from "@/utils/moreMenu/TaskPerformerHelper.ts";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue";
import openWizardEditorDialog from "@/utils/wizardEditors/openWizardEditorDialog.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ListViewPerformerHelper from "@/utils/moreMenu/ListViewPerformerHelper.ts";
import GUIActionInfo = namaTypes.GUIActionInfo;
import GUIActionResult = namaTypes.GUIActionResult;

export default class ActionPerformerUtil {
  private static actionsInProgress: Record<string, ActionInProgress> = {};

  static async calcDefaultsOf(action: ActionPlaceholder, callback: (fields: namaTypes.DTOFilledQuestionField[]) => void, helper: ActionPerformerHelper) {
    const actionId = action.id!;
    let actionInfo = helper.entityMetadata().entityActions?.actions?.find(l => l.id === actionId);
    if (!actionInfo || !actionInfo.actionInitializerClassName) {
      callback([]);
      return;
    }
    let parameters =
      new GUIActionParamsAndRow(actionId, []).invoke(actionInfo, helper).parameters;
    let settings: AxiosRequestConfig = {
      data: NamaCommon.stringify(parameters),
      params: {actionInitializerClassName: actionInfo.actionInitializerClassName}
    }
    let questionFields = await NamaCommon.namaFetch(NamaURLS.CalcGuiActionDefaults, settings)
    callback(questionFields);
    return;
  }

  static async performAction(action: ActionPlaceholder, questionFields: namaTypes.DTOFilledQuestionField[], metaData: namaTypes.NaMaMetaData
    , helper: ActionPerformerHelper): Promise<void> {
    const actionId = action.id!;
    let actionInfo: GUIActionInfo | undefined = metaData.entityActions?.actions?.find(l => l.id === actionId);
    if (!actionInfo)
      return;
    let actionParamsAndRow: GUIActionParamsAndRow = new GUIActionParamsAndRow(actionId, questionFields)
      .invoke(actionInfo, helper, true);
    let result: namaTypes.GUIActionResult = await NamaCommon.namaFetch(NamaURLS.PERFORM_Gui_Action, {data: actionParamsAndRow.parameters});
    await this.processGUIActionResult(result, actionParamsAndRow.contextRow, helper);
  }

  static addAction(pendingResponseId: string, row: number, helper: ActionPerformerHelper) {
    this.actionsInProgress[pendingResponseId] = {row, helper};
  }

  static removeAction(pendingResponseId: string | undefined) {
    if (pendingResponseId)
      delete this.actionsInProgress[pendingResponseId];
  }

  static async processAsyncGUIActionResult(asyncResult: GUIActionResult) {
    if (!asyncResult.pendingResponseId)
      return;
    const row = this.actionsInProgress[asyncResult.pendingResponseId]?.row ?? -1;
    let helper = this.actionsInProgress[asyncResult.pendingResponseId]?.helper;
    if (!helper)
      helper = new TaskPerformerHelper();
    let response = await NamaCommon.namaFetch(NamaURLS.CHECK_FOR_PENDING_GUI_ACTION_RESULT, {params: {pendingResponseId: asyncResult.pendingResponseId}})
    if (response && !response.processing) {
      await ActionPerformerUtil.processGUIActionResult(response, row, helper);
      this.removeAction(response.pendingResponseId);
    }
  }

  static async processGUIActionResult(result: namaTypes.GUIActionResult, contextRow: number | undefined, helper: ActionPerformerHelper) {
    let urls: string[];
    if (result) {
      if (result.type) {
        switch (result.type) {
          case GUIActionResultType.NewRecord:
            let place: EditPlace | undefined = PlaceUtils.newPlace(result.realObject ? result.realObject[CommonFieldIds.ENTITY_TYPE] : undefined, result.realObject);
            if (!place || !result.realObject)
              break;
            place.tabId = result.tabId;
            place.forceRefreshKey = Date.now().toString();
            await router.push({
              name: RouteNames.EDIT_VIEW_ROUTE,
              query: place.fetchQuery()
            });
            break;
          case GUIActionResultType.UpdateList:
            helper.onGuiActionUpdateList(result.updateList, contextRow);
            break;
          case GUIActionResultType.ExternalURL:
            urls = (result.externalURL ?? '').split(CommonConstants.URLS_SEPERATOR);
            for (let url of urls) {
              if (result.inBrowserPopUpWindow)
                window.open(url, "NamaPopup", "status=no,location=no,toolbar=no,menubar=no,width=1200,height=1000");
              else
                window.open(url, "_blank", "");
            }
            break;
          case GUIActionResultType.InternalURL:
            urls = (result.internalURL ?? '').split(CommonConstants.URLS_SEPERATOR);
            if (result.inPopup)
              this.openInPopup(urls);
            else if (result.inNewWindow)
              for (let url of urls)
                window.open(url, "_blank", "");
            else if (result.inBrowserPopUpWindow)
              for (let url of urls) {
                if (openWizardEditorDialog(url)) continue;
                window.open(url, "NamaPopup", "status=no,location=no,toolbar=no,menubar=no,width=1200,height=1000");
              }
            else if (urls.length > 1)
              NamaPrinting.printNext(urls, 0);
            else
              GUIUtilities.goToInternalURL(result.internalURL ?? '');
            break;
          case GUIActionResultType.PopupListView:
            if (result.listView) {
              Dialog.create({
                component: ListDialog,
                componentProps: {
                  guiActionResult: result,
                  presenter: helper.fetchPresenter()
                }
              });
            }
            break;
          case GUIActionResultType.BarcodePrint:
            GUIUtilities.printBarcodeAction(result.printBarCode);
            break;
          case GUIActionResultType.GoToPlace:
            // TODO :: helper.beforeGoToPlace();
            if (result.place) {
              // let place = result.place.startsWith('#') ? result.place.substring(1) : result.place;
              // place = place.startsWith('/') ? place.substring(1) : place;
              await router.push({path: '/' + result.place});
            }
            break;
          case GUIActionResultType.NewRecordInPopup:
            Dialog.create({
              component: QuickCreatorDialog,
              componentProps: {
                newObject: result.realObject,
                tabId: result.tabId
              }
            });
            break;
          case GUIActionResultType.TextToCopy:
            GUIUtilities.showTextToCopy(result.textToCopy);
            break;
        }
        if (result.type === GUIActionResultType.UpdateList && result.updateList) {
          helper.onGuiActionUpdateList(result.updateList, contextRow);
        }
      }
      let hasErrors: boolean = false;
      let hasWarnings: boolean = false;
      const isHasErrorsOrWarning = this.checkIfHasErrorsOrWarning(result, hasErrors, hasWarnings);
      hasErrors = isHasErrorsOrWarning.hasErrors;
      hasWarnings = isHasErrorsOrWarning.hasWarnings;
      if (result.afterPerformBehavior === AfterPerformBehavior.Refresh) {
        await helper.refresh(hasErrors, hasWarnings);
      }
    }
    if (result.pendingResponseId && !result.type) {
      if (result.processing && !result.messageIsError && !result.failure && !result.errors) {
        this.addAction(result.pendingResponseId, contextRow ?? -1, helper);
        this.ShowProgressDialog(result, helper);
      }
    } else if (result.pendingResponseId && ObjectChecker.isEmptyOrNull(result.processing)) {
      this.checkForPendingGUIActionResponse(result.pendingResponseId, helper, contextRow);
    }
  }

  private static checkIfHasErrorsOrWarning(result: namaTypes.GUIActionResult, hasErrors: boolean, hasWarnings: boolean) {
    if (result.messages && result.messages.length > 0) {
      for (let message of result.messages) {
        if (!message)
          continue;
        if (result.messageIsError) {
          hasErrors = true;
          useErrorsStore().pushErrorStr(Translator.translateNaMaText(message))
        } else {
          hasWarnings = true;
          useMessagesStore().pushMessageStr(Translator.translateNaMaText(message))
        }
      }
    }
    if (result.errors && result.errors.length > 0) {
      hasErrors = true;
      useErrorsStore().pushErrors(result.errors);
    }
    if (result.failure && result.failure.errors && result.failure.errors.length > 0) {
      hasErrors = true;
      useErrorsStore().pushErrors(result.failure.errors);
    }
    return {hasErrors, hasWarnings};
  }

  static ShowProgressDialog(result: namaTypes.GUIActionResult, helper: ActionPerformerHelper) {
    Dialog.create({
      component: ProgressDialog,
      componentProps: {
        pendingResponseId: result.pendingResponseId,
        title: result.progressTitle,
      }
    }).onOk(async (asyncResult: GUIActionResult) => {
      await ActionPerformerUtil.processAsyncGUIActionResult(asyncResult);
      if (helper instanceof ListViewPerformerHelper && ObjectChecker.isEmptyOrNull(asyncResult) && result.iconConstant == "deleteSelected")
        helper.gridApi.deselectAll();
    }).onCancel(async () => {
      await this.killTask(result);
    })
  }

  static checkForPendingGUIActionResponse(pendingResponseId: string, helper: ActionPerformerHelper, contextRow?: number) {
    let handler = async () => {
      let response = await NamaCommon.namaFetch(NamaURLS.CHECK_FOR_PENDING_GUI_ACTION_RESULT, {params: {pendingResponseId}})
      if (response && !response.processing) {
        await this.processGUIActionResult(response, contextRow, helper);
        this.removeAction(response.pendingResponseId);
      } else {
        setTimeout(handler, 1500);
      }
    }
    setTimeout(handler, 1500);
  }

  static async killTask(result: GUIActionResult) {
    Dialog.create({
      component: ConfirmDialog,
      componentProps: {
        title: Translator.translateId('killTask'),
        confirmationText: Translator.translateId('confirmKillTask'),
        ok: Translator.translateId("ok"),
        cancel: Translator.translateId("cancel"),
        isPersistent: true
      }
    }).onOk(async () => {
      await NamaCommon.namaFetch(NamaURLS.KillTaskById, {params: {taskId: result.pendingResponseId}});
      await NamaCommon.waitFor(1500);
      await ActionPerformerUtil.processAsyncGUIActionResult(result);
    });
  }

  static openInPopup(urls: string[]) {
    let url: string = urls[0];
    if (url.startsWith("#"))
      url = url.substring(1);
    if (url.startsWith('/edit') || url.startsWith('edit'))
      return;
    /*TODO::*/
    /*
        GUIUtilities.goToEditPlaceInPopup(f -> {
        }, f -> {
        }, (EditPlace) place);
    */
  }
}

